import { ProviderGroup } from '../../../../lib/helpers/SmartSendBulkActionApplier';
import { useCallback } from 'react';
import { GetAccountId } from '../../../../lib/helpers/AuthHelper';
import { getSelectedQuoteOption } from '../../generateProtectionKey';
import { SumNumber } from '../../../../lib/helpers/ArrayHelper';
import { ExtraBase, ShipResponse } from '~/lib/api/smartsend/types';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useAuth } from 'react-oidc-context';
import { useMeApi } from '~/lib/api/me/use-me-api';
import { MyExtendedDetails } from '~/lib/api/me/types';
import { User } from 'oidc-client-ts';
import { FindCountryName } from '~/apps/smartsend/lib/cache/CountryCache';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import {
  CoverExtra,
  DeliveryGuaranteeExtra,
  DriverBringsLabelExtra,
  ExtendedBaseCoverExtra,
  PrintInStoreExtra,
  SignatureExtra,
  SmsExtra
} from '~/lib/consts/extras';

type TransactionProduct = {
  sku: string;
  name: string;
  category: string;
  price: number;
  quantity: number;
  courierName?: string;
  deliveryAddressCountry?: string;
};

type DataLayerTransaction = {
  event: string;
  userEmail?: string;
  userName?: string;
  customerId: string;
  userLoggedIn: boolean;
  isBulkUpload: boolean;
  parcelWeight: number;
  transactionId: string;
  transactionTotal: number;
  transactionTotalIncVat: number;
  transactionCurrencyCode: string;
  transactionVoucherCode: string;
  vatAmount: number;
  transactionAge: number;
  transactionTimestampUtc: string;
  transactionProducts: TransactionProduct[];
  isFirstSmartSendOrder?: boolean;
  isFirstOrder?: boolean;
};

const createPurchaseEventLegacyPayload = (
  res: ShipResponse,
  group: ProviderGroup,
  myDetails: MyExtendedDetails,
  user: User
) => {
  const transationalProducts: TransactionProduct[] = [];

  group.Items.forEach((item) => {
    const price = getSelectedQuoteOption(item, item.State.validation.QuoteState.Quote);

    const extrasRecords = price.Extras.map((e) => ({
      category: 'upsell',
      sku: e.Id,
      name: e.Id,
      price: roundToCurrency(e.Total || 0),
      quantity: 1
    }));
    const totalExtrasCost = roundToCurrency(SumNumber(extrasRecords.map((x) => x.price)));

    const itemRecord = {
      sku: item.Service.Id,
      category: item.Recipient.CountryIsoCode,
      name: item.Service.Name,
      price: (price.TotalPrice || 0) - totalExtrasCost,
      quantity: 1,
      deliveryAddressCountry: item.Recipient.CountryIsoCode
    };

    // 1 Record per order
    transationalProducts.push(itemRecord);

    // Plus extras on new item
    for (const extra of extrasRecords) {
      transationalProducts.push(extra);
    }
  });

  const payload: DataLayerTransaction = {
    event: 'purchase',
    userEmail: myDetails?.email,
    userName: myDetails ? `${myDetails.forename} ${myDetails.surname}` : undefined,
    customerId: GetAccountId(user.profile),
    userLoggedIn: true,
    isBulkUpload: true,
    transactionAge: 0,
    parcelWeight: SumNumber(group.Items.map((x) => x.PackageDimensions.Weight || 0)),
    transactionId: res.Reference,
    transactionCurrencyCode: group.Items[0]?.CurrencyCode || 'GBP',
    transactionTimestampUtc: new Date().toUTCString(),
    transactionTotal: res.TotalPriceExVat,
    transactionTotalIncVat: res.TotalPrice,
    vatAmount: res.TotalVat,
    transactionVoucherCode: res.DiscountCode,
    transactionProducts: transationalProducts,
    isFirstSmartSendOrder: !!res.Tags?.includes('first-smartsend-order'),
    isFirstOrder: !!res.Tags?.includes('first-order')
  };

  return payload;
};

const extraIdToName = (extra: ExtraBase) => {
  switch (extra.Id) {
    case CoverExtra:
      return 'Extra Comp. Cover';

    case ExtendedBaseCoverExtra:
      return 'Extended Base Cover';

    case DriverBringsLabelExtra:
      return 'Driver Brings Label';

    case PrintInStoreExtra:
      return 'Print In Store';

    case SmsExtra:
      return 'Delivery Alert';

    case SignatureExtra:
      return 'Signature';

    case DeliveryGuaranteeExtra:
      return 'Delivery Guarantee';

    default:
      return undefined;
  }
};

const extraIdToAnalyticsId = (extra: ExtraBase) => {
  switch (extra.Id) {
    case CoverExtra:
      return 'protection:full';

    case ExtendedBaseCoverExtra:
      return 'protection:base';

    case DriverBringsLabelExtra:
      return 'driverbringslabel';

    case PrintInStoreExtra:
      return 'printinstore';

    case SmsExtra:
      return 'smsalert';

    case SignatureExtra:
      return 'signature';

    case DeliveryGuaranteeExtra:
      return 'deliveryguarantee';

    default:
      return undefined;
  }
};

const createPurchaseEventGA4Payload = (
  res: ShipResponse,
  group: ProviderGroup,
  myDetails: MyExtendedDetails,
  user: User
) => {
  const transationalProducts = [];

  const coupon = res.DiscountCode || undefined;

  group.Items.forEach((item) => {
    const price = getSelectedQuoteOption(item, item.State.validation.QuoteState.Quote);
    const service = item.State?.validation?.QuoteState?.Quote?.Service;

    if (!service) {
      return;
    }

    const extrasRecords = price.Extras.map((e) => ({
      item_category: 'Extra',
      item_brand: 'Parcel2Go',
      price: roundToCurrency(e.Total || 0),
      item_id: extraIdToAnalyticsId(e),
      item_name: extraIdToName(e)
    }));

    const totalExtrasCost = roundToCurrency(SumNumber(extrasRecords.map((x) => x.price)));

    const itemRecord = {
      item_id: item.Service.Id,
      item_name: item.Service.Name,
      item_brand: service?.CourierName,
      item_category: item.Recipient.CountryIsoCode === item.Sender.CountryIsoCode ? 'Domestic' : 'International',
      item_category2: FindCountryName(item.Sender.CountryIsoCode),
      item_category3: service?.CollectionType === 'Collection' ? 'Collection' : 'Drop Off',
      item_category4: price.Extras.some((x) => [PrintInStoreExtra, DriverBringsLabelExtra].includes(x.Id))
        ? 'Printer Not Required'
        : 'Printer Required',
      price: (price.TotalPriceExVat || 0) - totalExtrasCost,
      coupon: coupon
    };

    // 1 Record per order
    transationalProducts.push(itemRecord);

    // Plus extras on new item
    for (const extra of extrasRecords) {
      transationalProducts.push(extra);
    }
  });

  const payload = {
    ecommerce: {
      currency: group.Items[0]?.CurrencyCode || 'GBP',
      value: res.TotalPriceExVat,
      transaction_id: res.Reference,
      tax: res.TotalVat,
      discount: res.TotalDiscount,
      coupon: coupon,
      items: transationalProducts,
      user_name: myDetails ? `${myDetails.forename} ${myDetails.surname}` : undefined,
      user_email: myDetails?.email,
      customer_id: GetAccountId(user.profile),
      is_logged_in: true,
      is_smart_send_order: true,
      paid_date: new Date().toUTCString(),
      is_first_order: !!res.Tags?.includes('first-order'),
      is_first_smart_send_order: !!res.Tags?.includes('first-smartsend-order'),
      vip_status: myDetails.metadata?.supportType
    }
  };

  return payload;
};

const roundToCurrency = (value: number) => Math.round(value * 100) / 100;

export const useRecordPurchaseAnalytics = () => {
  const { user } = useAuth();
  const { getMyExtendedDetails } = useMeApi();
  const { track } = useSmartSendAnalytics();

  const record = useCallback(
    async (res: ShipResponse, group: ProviderGroup) => {
      try {
        const version = SettingsCache.settings.Features.includes('PaymentAnalyticsGA4')
          ? 'GA4'
          : SettingsCache.settings.Features.includes('PaymentAnalytics')
            ? 'LEGACY'
            : null;

        if (!window || !version) {
          return;
        }

        const myDetails = await getMyExtendedDetails();

        switch (version) {
          case 'LEGACY': {
            // Current Behaviour
            const payload = createPurchaseEventLegacyPayload(res, group, myDetails?.result, user);
            if (payload) {
              window['dataLayer'] = window['dataLayer'] || [];
              window['dataLayer'].push(payload);
            }

            // Store GA4 Version in console for comparison while both are live
            console.log('Analytics GA4:', createPurchaseEventGA4Payload(res, group, myDetails?.result, user));
            break;
          }

          case 'GA4': {
            // Push stright to the analytics provider which also pushed to dataLayer.
            track({
              type: 'purchase',
              payload: createPurchaseEventGA4Payload(res, group, myDetails?.result, user)
            });
            break;
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    [user, getMyExtendedDetails, track]
  );

  return { record } as const;
};
